import { CalendarClock, Layers, ShieldCheck, Zap } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { cn, formatDateValue } from '@/lib/utils';

import type { PlanInfo } from '../modulos.types';

interface PlanCardProps {
  plan: PlanInfo | null;
  totalModulos: number;
  className?: string;
}

const MESES = 30;

/**
 * Resumen del plan contratado (nombre, vigencia y cantidad de módulos).
 * Replica el bloque de plan del panel anterior.
 */
export function PlanCard({ plan, totalModulos, className }: PlanCardProps) {
  if (!plan) {
    return (
      <div className={cn('rounded-card border border-dashed border-ink-200 bg-white/70 p-5', className)}>
        <p className="flex items-center gap-2 text-sm font-semibold text-ink-700">
          <Layers className="size-4 text-ink-400" aria-hidden="true" />
          Sin plan asignado
        </p>
        <p className="mt-1.5 text-xs leading-relaxed text-ink-500">
          La empresa no tiene un plan configurado, por lo que se muestra el catálogo completo de
          módulos.
        </p>
      </div>
    );
  }

  const porVencer =
    plan.dias_restantes !== null && plan.dias_restantes >= 0 && plan.dias_restantes <= MESES;

  return (
    <div
      className={cn(
        'rounded-card border border-ink-100 bg-white p-5 shadow-soft',
        'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-accent-600 text-white"
        >
          <Zap className="size-5" />
        </span>

        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-bold tracking-tight text-ink-900">{plan.nombre}</h2>

            {plan.activo ? (
              <Badge variant="success">Activo</Badge>
            ) : (
              <Badge variant="danger">Inactivo</Badge>
            )}

            {porVencer ? <Badge variant="warning">Por vencer</Badge> : null}
          </div>

          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-500">
            <span className="flex items-center gap-1.5">
              <Layers className="size-3.5 text-ink-400" aria-hidden="true" />
              {totalModulos} módulos habilitados
            </span>

            {plan.fecha_fin ? (
              <span className="flex items-center gap-1.5">
                <CalendarClock className="size-3.5 text-ink-400" aria-hidden="true" />
                Renueva el {formatDateValue(plan.fecha_fin)}
              </span>
            ) : null}

            {plan.max_usuarios > 0 ? (
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="size-3.5 text-ink-400" aria-hidden="true" />
                Hasta {plan.max_usuarios} usuarios
              </span>
            ) : null}
          </p>
        </div>
      </div>

      {plan.dias_restantes !== null ? (
        <div className="shrink-0 text-left sm:text-right">
          <p className="font-mono text-2xl font-bold tracking-tight text-ink-900 tabular-nums">
            {plan.dias_restantes}
          </p>
          <p className="text-xs text-ink-400">
            {plan.dias_restantes === 1 ? 'día restante' : 'días restantes'}
          </p>
        </div>
      ) : null}
    </div>
  );
}
